<template>
  <!-- eslint-disable vue/no-v-html -->
  <svg
    v-if="isCoBrowsing && inlinedContent"
    :class="['vc-icon shrink-0', sizeClass]"
    :fill="inlinedFill"
    :style="style"
    :viewBox="inlinedViewBox"
    v-html="inlinedContent"
  ></svg>
  <!-- eslint-enable vue/no-v-html -->
  <svg v-else :class="['vc-icon shrink-0', sizeClass]" :style="style">
    <use :href="`${path}${name}.svg?v=${version}#icon`"></use>
  </svg>
</template>

<script setup lang="ts">
import DOMPurify from "dompurify";
import { computed, ref, watch } from "vue";
import { isCoBrowsingActive } from "@/shared/live-person";

interface IProps {
  name?: string;
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | number;
  path?: string;
}

const props = withDefaults(defineProps<IProps>(), {
  name: "document-text",
  path: "/static/thorlabs/icons/basic/",
});

// Shared in-memory cache for fetched SVG content across all VcIcon instances
const svgCache = new Map<string, { viewBox: string; content: string; fill: string | undefined }>();

const style = computed(() =>
  typeof props.size === "number"
    ? {
        width: `${props.size}px`,
        height: `${props.size}px`,
      }
    : {},
);

const sizeClass = computed(() => (typeof props.size === "string" ? `vc-icon--size--${props.size}` : ""));
const d = new Date();
const version = `${d.getFullYear()}${d.getMonth() + 1}${d.getDate()}`;

const isCoBrowsing = isCoBrowsingActive;
const inlinedContent = ref("");
const inlinedViewBox = ref("0 0 24 24");
const inlinedFill = ref<string>();

const svgUrl = computed(() => `${props.path}${props.name}.svg?v=${version}`);

const loadSvg = async (url: string) => {
  const cached = svgCache.get(url);
  if (cached) {
    if (url === svgUrl.value && isCoBrowsing.value) {
      inlinedContent.value = cached.content;
      inlinedViewBox.value = cached.viewBox;
      inlinedFill.value = cached.fill;
    }
    return;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return;
    }
    const text = await response.text();
    const sanitizedText = DOMPurify.sanitize(text, { USE_PROFILES: { svg: true, svgFilters: true } });
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedText, "image/svg+xml");
    const svg = doc.querySelector("svg");
    if (svg) {
      const vb = svg.getAttribute("viewBox") || "0 0 24 24";
      const inner = svg.innerHTML;
      const fill = svg.getAttribute("fill") ?? undefined;
      svgCache.set(url, { viewBox: vb, content: inner, fill });
      if (url === svgUrl.value && isCoBrowsing.value) {
        inlinedContent.value = inner;
        inlinedViewBox.value = vb;
        inlinedFill.value = fill;
      }
    }
  } catch {
    // Silently fail — falls back to <use href> via v-else
  }
};

// Only fetch and inline SVGs when co-browsing is active
watch(
  [svgUrl, isCoBrowsing],
  ([url, active]) => {
    inlinedContent.value = "";
    inlinedViewBox.value = "0 0 24 24";
    inlinedFill.value = undefined;
    if (active) {
      void loadSvg(url);
    }
  },
  { immediate: true },
);
</script>

<style lang="scss">
.vc-icon-with-label {
  @apply inline-flex items-center gap-2;

  &--start {
    @apply flex-row-reverse;
  }

  &--end {
    @apply flex-row;
  }

  &--top {
    @apply flex-col-reverse;
  }

  &--bottom {
    @apply flex-col;
  }

  &__text {
    @apply text-current;
  }
}

.vc-icon {
  --size: var(--vc-icon-size, 1.5rem);
  --color: var(--vc-icon-color, inherit);

  $self: &;

  @apply inline-block align-middle w-[--size] h-[--size] leading-none text-[--color];

  &--size {
    &--xxs {
      @apply w-2.5 h-2.5;
    }

    &--xs {
      @apply w-3.5 h-3.5;
    }

    &--sm {
      @apply w-5 h-5;
    }

    &--lg {
      @apply w-10 h-10;
    }

    &--xl {
      @apply w-12 h-12;
    }

    &--xxl {
      @apply w-16 h-16;
    }
  }

  @at-root .vc-button {
    $icon: "";

    &--icon {
      $icon: &;
    }

    #{$self} {
      &:first-child:not(:last-child) {
        @apply me-1;
      }

      &:last-child:not(:first-child) {
        @apply ms-1;
      }
    }

    &__slot {
      #{$icon} & {
        & > #{$self} {
          @apply mx-0 #{!important};
        }

        & > *:not(#{$self}) {
          @apply hidden;
        }
      }
    }
  }
}
</style>
